import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { tracker } from "../../../../analytics";
import {
  Box, Typography, Chip, CircularProgress,
  Snackbar, Alert, IconButton, Tooltip, Collapse, useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditorContext from "../../../../contexts/EditorContext";
import { formatHtmlForDisplay } from "../../../../utils/utils";

const Suggestion = ({ contractId, startSuggestions }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t, i18n } = useTranslation();
  const clauses = useSelector((state) => state.contract.suggestedClauses || []);
  const loadingSuggestedClause = useSelector((state) => state.contract.loadingSuggestedClause);
  const hasSuggestions = clauses && clauses.length > 0;
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
  const isRTL = i18n.language === "ar";
  const editorRef = useContext(EditorContext);
  const contract = useSelector(state => state?.contract?.contract);
  const currentUser = useSelector(state => state.auth?.user?.user);
  const contractStatus = contract?.status;
  const userRole = (contract?.collaborators || []).find(c => c.id === currentUser?.id)?.role;
  const isLocked = ['PENDING_SIGNATURE', 'SIGNED'].includes(contractStatus) || userRole === 'reviewer';

  const handleGetSuggestions = async () => {
    if (contractId) await startSuggestions();
  };

  const handleAddClause = (clause) => {
    if (isLocked) return;
    try {
      const editor = editorRef?.current?.getEditor();
      if (!editor) {
        setNotification({ open: true, message: t("suggestions.editorNotAvailable"), severity: 'error' });
        return;
      }
      const clauseContent = `<h3>${clause.title}</h3><p>${clause.description}</p>`;
      const currentContent = editor.getHTML();
      const contentToInsert = currentContent?.trim() ? `${currentContent}<p></p>${clauseContent}` : clauseContent;
      editor.commands.setContent(contentToInsert);
      editor.commands.focus('end');
      ReactGA.event({ category: 'Suggestions', action: 'Add Clause', label: clause.title });
      tracker.event({ category: 'Suggestions', action: 'Add Clause', label: clause.title });
      setNotification({ open: true, message: t("suggestions.clauseAdded"), severity: 'success' });
    } catch (error) {
      console.error("Error adding clause to editor:", error);
      setNotification({ open: true, message: t("suggestions.errorAddingClause"), severity: 'error' });
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", direction: isRTL ? 'rtl' : 'ltr' }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Chip
          label={t("riskAnalysis.Chip")}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
            color: theme.palette.primary.main,
            fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 0.75,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {t("suggestions.title")}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mt: 0.25 }}>
              {t("suggestions.subtitle")}
            </Typography>
          </Box>
          <Tooltip title={hasSuggestions ? t("suggestions.refresh") : t("suggestions.getClauseSuggestions")}>
            <span>
              <IconButton
                onClick={handleGetSuggestions}
                disabled={loadingSuggestedClause}
                size="small"
                sx={{
                  mt: 0.25, width: 30, height: 30,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  borderRadius: '8px', color: theme.palette.text.secondary,
                  transition: 'all 0.15s',
                  '&:hover': { color: theme.palette.text.primary },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                {loadingSuggestedClause ? <CircularProgress size={13} color="inherit" /> : hasSuggestions ? <RefreshIcon sx={{ fontSize: 15 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Suggestions list ── */}
      {hasSuggestions ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {clauses.map((clause, index) => {
            const isExpanded = expandedIndex === index;
            return (
              <Box
                key={index}
                sx={{
                  borderRadius: '10px',
                  border: `1px solid ${isExpanded ? alpha(theme.palette.primary.main, 0.35) : alpha(theme.palette.divider, 0.6)}`,
                  backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
                  overflow: 'hidden',
                  transition: 'border-color 0.16s',
                }}
              >
                {/* Summary row */}
                <Box
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 1.5, py: 1.1,
                    cursor: 'pointer', userSelect: 'none',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.82rem', textAlign: 'left', pr: 1 }}>
                    {clause.title}
                  </Typography>

                  <Tooltip title={isLocked ? (userRole === 'reviewer' ? t("reviewer_locked_tooltip", "Vous êtes en mode relecture") : t("contract_locked_tooltip")) : t("suggestions.addClause")} arrow placement="top">
                    <span>
                    <IconButton
                      size="small"
                      disabled={isLocked}
                      onClick={(e) => { e.stopPropagation(); handleAddClause(clause); }}
                      sx={{
                        width: 24, height: 24, borderRadius: '6px', mr: 0.5,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
                        '&.Mui-disabled': { opacity: 0.35 },
                      }}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={clause.description} arrow placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ color: alpha(theme.palette.text.secondary, 0.6), width: 22, height: 22, mr: 0.5 }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>

                  <Box sx={{
                    color: isExpanded ? theme.palette.primary.main : theme.palette.text.disabled,
                    display: 'flex', transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}>
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>

                {/* Expanded content */}
                <Collapse in={isExpanded}>
                  <Box sx={{
                    px: 1.5, py: 1.25,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
                  }}>
                    <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.65, color: theme.palette.text.secondary, textAlign: 'left' }}>
                      {formatHtmlForDisplay(clause.description)}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ) : !loadingSuggestedClause && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '12px',
            backgroundColor: alpha(theme.palette.text.secondary, 0.08),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LightbulbOutlinedIcon sx={{ fontSize: 24, color: theme.palette.text.disabled }} />
          </Box>
          <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary, textAlign: 'center' }}>
            {t("suggestions.clickAboveGetClauseSuggestions")}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(n => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(n => ({ ...n, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Suggestion;
